import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PartyPopper, ArrowDown } from "lucide-react";
import { ShareBar } from "./ShareBar";
import { campaignUrl, campaignShareUrl } from "../lib/campaign";
import { EmSwash } from "./EmSwash";

/**
 * DashboardApprovalBanner — the very first thing a returning student
 * sees the first time they land on the dashboard AFTER their draft
 * has been approved and pushed live.
 *
 * Mirrors DashboardRejectionBanner's placement (top of dashboard,
 * above the greeting) — but green, celebratory, and carries the
 * shareable link + a ShareBar so the student can push it to their
 * network in one tap.
 *
 * "First time" is per-campaign, stored in localStorage under
 * `mra_seen_approval_<campaign_id>`. Once dismissed, the banner does
 * not reappear for that campaign on this device.
 */
const KEY_PREFIX = "mra_seen_approval_";

export const DashboardApprovalBanner = ({ campaign, studentFirstName }) => {
  // ALL hooks must run before any early return — React hooks rules.
  const [dismissed, setDismissed] = useState(false);

  const isEligible = campaign && campaign.status === "live" && campaign.id;
  const storageKey = campaign?.id ? `${KEY_PREFIX}${campaign.id}` : null;

  useEffect(() => {
    if (!isEligible || !storageKey) return;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(storageKey) === "1") {
      setDismissed(true);
    }
  }, [isEligible, storageKey]);

  const dismiss = () => {
    if (typeof window !== "undefined" && storageKey) {
      window.localStorage.setItem(storageKey, "1");
    }
    setDismissed(true);
  };

  const shareUrl = useMemo(() => {
    if (!isEligible) return "";
    if (typeof window === "undefined") return `/campaign/${campaign.id}`;
    return campaignUrl(campaign);
  }, [isEligible, campaign?.id, campaign?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isEligible || dismissed) return null;

  const firstName = campaign.title || "your campaign";

  return (
    <AnimatePresence>
      <motion.section
        role="status"
        data-testid="dashboard-approval-banner"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8, height: 0 }}
        transition={{ duration: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative overflow-hidden rounded-2xl border-2 border-grass bg-grass/[0.06] p-6 lg:p-8"
      >
        <span aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-grass/18 blur-3xl" />
        <span aria-hidden className="pointer-events-none absolute -left-12 -bottom-14 h-40 w-40 rounded-full bg-yellow/25 blur-3xl" />

        {/* Confetti — small yellow/grass dots that drift in on mount */}
        <ConfettiPuff />

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="dashboard-approval-dismiss"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-olive transition-colors hover:bg-ink/5 hover:text-ink"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <div className="relative flex items-start gap-5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-grass text-sand shadow-[0_10px_30px_-8px_rgba(56,142,60,0.5)]">
            <PartyPopper className="h-6 w-6" strokeWidth={2.2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-2 pr-8">
              <span
                className="inline-flex items-center text-[13px] font-semibold uppercase tracking-[0.22em] text-grass"
                data-testid="dashboard-approval-eyebrow"
              >
                You&rsquo;re approved
              </span>
              {campaign.reviewed_at && (
                <span className="text-[13px] font-medium uppercase tracking-[0.18em] text-olive">
                  {new Date(campaign.reviewed_at).toLocaleDateString(undefined, {
                    month: "short", day: "numeric",
                  })}
                </span>
              )}
            </div>
            <h2 className="mt-2 font-serif text-3xl leading-[1.05] text-ink" data-testid="dashboard-approval-headline">
              <EmSwash color="text-grass">{firstName}</EmSwash> is live.
            </h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-olive">
              Here&rsquo;s your shareable link. Every text, DM, and story you send starts real support — the biggest early donors almost always come from the student&rsquo;s own network.
            </p>

            {/* Shareable URL card */}
            <div
              data-testid="dashboard-approval-link-card"
              className="mt-5 rounded-lg border-2 border-grass/25 bg-bone p-4"
            >
              <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                Your public campaign
              </p>
              <p
                className="mt-1 truncate font-serif text-3xl text-ink"
                data-testid="dashboard-approval-link"
                title={shareUrl}
              >
                {shortHost(shareUrl)}
              </p>
            </div>

            {/* Share row */}
            <div className="mt-5">
              <ShareBar
                url={campaignShareUrl(campaign)}
                displayUrl={shareUrl}
                studentFirstName={studentFirstName || "your student"}
                campaignTitle={campaign.title}
                goalCents={campaign.goal_cents}
                variant="light"
                testidPrefix="dashboard-approval-share"
              />
            </div>

            <a
              href="#payouts"
              data-testid="dashboard-approval-payouts-cta"
              onClick={(e) => { e.preventDefault(); document.getElementById("payouts")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
            >
              Next: set up your payout account <ArrowDown className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

// ─── helpers ────────────────────────────────────────────────────────
const shortHost = (url) => {
  try { const u = new URL(url); return `${u.host}${u.pathname}`; }
  catch { return url; }
};

const ConfettiPuff = () => {
  const dots = [
    { c: "bg-yellow",    l: "8%",  d: 0.05 },
    { c: "bg-grass",     l: "22%", d: 0.20 },
    { c: "bg-tangerine", l: "40%", d: 0.35 },
    { c: "bg-cobalt",    l: "58%", d: 0.15 },
    { c: "bg-blossom",   l: "74%", d: 0.30 },
    { c: "bg-yellow",    l: "88%", d: 0.10 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-16 overflow-hidden">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          initial={{ y: -40, opacity: 0, rotate: 0 }}
          animate={{ y: 40, opacity: [0, 1, 0], rotate: 180 }}
          transition={{ duration: 2.6, delay: d.d, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute h-1.5 w-1.5 rounded-full ${d.c}`}
          style={{ left: d.l }}
        />
      ))}
    </div>
  );
};

